import { isEmpty } from 'lodash/fp';
import { defineAsyncComponent, shallowRef } from 'vue';
import { type RouteLocationNormalizedLoaded } from 'vue-router';

import { getHashPairsFromRouter } from '@exchange/helpers/query-param-to-string';
import { accountService } from '@exchange/libs/account/service/src';
import { modalService, modalVariant, type ModalVariant } from '@exchange/libs/modals/src';
import { DepositFiatAction } from '@exchange/libs/transactions/deposit/src';
import DepositStatusModal from '@exchange/libs/transactions/deposit/src/lib/DepositStatusModal.vue';
import { fundsService } from '@exchange/libs/transactions/funds/service/src';
import router from '@exchange/routing/router';

const WithdrawExternalCancel = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-withdraw-external-cancel' */ './WithdrawExternalCancel.vue'));
const WithdrawExternalConfirm = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-withdraw-external-confirm' */ './WithdrawExternalConfirm.vue'));
const WithdrawExternalInvalidLink = defineAsyncComponent(() => import(/* webpackChunkName: 'chunk-withdraw-external-invalid' */ './WithdrawExternalInvalidLink.vue'));

const useTransaction = (modalsVariant: ModalVariant, waitForAccountId: () => Promise<string>) => {
  const confirmTransaction = async (transactionId: string, secret: string) => {
    await waitForAccountId();

    return fundsService.withdraw
      .confirm(transactionId, secret)
      .then(() => {
        modalService.substitute(
          shallowRef(WithdrawExternalConfirm),
          {
            variant: modalsVariant,
          },
          {
            'action-clicked': () => modalService.clear(),
            'view-history': () => {
              modalService.clear();
              router.replace({
                name: 'trading-accounts.transactions',
                params: { tab: 'withdraw' },
              });
            },
          },
          { variant: modalsVariant },
        );
      })
      .catch(() =>
        modalService.substitute(
          shallowRef(WithdrawExternalInvalidLink),
          {
            variant: modalsVariant,
          },
          {
            'action-clicked': () => modalService.clear(),
          },
          { variant: modalsVariant },
        ),
      );
  };

  const cancelTransaction = async (transactionId: string) => {
    await waitForAccountId();

    return fundsService.withdraw
      .cancel(transactionId)
      .then(() => {
        modalService.substitute(
          shallowRef(WithdrawExternalCancel),
          {
            variant: modalsVariant,
          },
          {
            'action-clicked': () => modalService.clear(),
          },
          { variant: modalsVariant },
        );
      })
      .catch(() =>
        modalService.substitute(
          shallowRef(WithdrawExternalInvalidLink),
          {
            variant: modalsVariant,
          },
          {
            'action-clicked': () => modalService.clear(),
          },
          { variant: modalsVariant },
        ),
      );
  };

  return {
    confirmTransaction,
    cancelTransaction,
  };
};

const useEmailFlow = () => {
  const modalsVariant = modalVariant.dark;
  const waitForAccountId = () => accountService.getAccountId({ useSubaccount: false });

  const { confirmTransaction, cancelTransaction } = useTransaction(modalsVariant, waitForAccountId);

  const handleDepositFiatExternalFlow = (isSuccess: boolean) => {
    modalService.show(
      shallowRef(DepositStatusModal),
      {
        variant: modalsVariant,
        depositStatus: isSuccess ? 'success' : 'error',
      },
      {
        'action-clicked': () => modalService.hide(),
        'view-transactions': () => {
          router.push({
            name: 'trading-accounts.transactions',
          });
          modalService.hide();
        },
      },
      { variant: modalsVariant },
    );
  };

  const orchestrateEmailFlow = async (route: RouteLocationNormalizedLoaded) => {
    const { action, transactionId, secret } = getHashPairsFromRouter(route);

    if (!isEmpty(action)) {
      /**
       * Transactions flow
       */
      if (!isEmpty(transactionId) && !isEmpty(secret)) {
        if (action === 'confirm') {
          await confirmTransaction(transactionId, secret);
        } else if (action === 'cancel') {
          await cancelTransaction(transactionId);
        }
      }

      /**
       * Deposit Fiat External flow
       * :alarm: payment providers tend to add ?something in the url which is not covered by getHashPairsFromRouter
       * so we need to potential `?` from the action, currency, payment, amount params
       */
      const getStrippedString = (str) => (str ? str.split('?')[0] : str);
      const strippedAction = getStrippedString(action);

      if (strippedAction === DepositFiatAction.FAILURE || strippedAction === DepositFiatAction.SUCCESS) {
        handleDepositFiatExternalFlow(strippedAction === DepositFiatAction.SUCCESS);
      }

      await router.replace({ name: 'exchange' });
    }
  };

  return {
    orchestrateEmailFlow,
  };
};

export default useEmailFlow;
